import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiCallStatus } from "../../utils/constants";
import _ from "lodash";
import { Alert, Backdrop, CircularProgress, Container } from "@mui/material";
import CheckoutSummary from "../../components/CheckoutSummary";
import { useCart } from "react-use-cart";
import {
	fetchAddressCheckoutData,
	fetchCheckoutData,
	fetchOrderCheckoutData
} from "../PageFunctions/checkoutFunctions";
import { createOrderData } from "../PageFunctions/orderFunctions";
import { useArgusAuth } from "../../hooks/useArgusAuth";


const CheckoutSummaryPage = ({
	genericDataLoaded,
	genericPageContent
}) => {
	const {isAuthenticated, isLoading} = useArgusAuth();
	const {items, emptyCart} = useCart();
	const location = useLocation();
	const navigate = useNavigate();
	const [checkoutData, setCheckoutData] = useState(null);
	const [addressCheckoutInfo, setAddressCheckoutInfo] = useState(null);
	const [orderCheckoutInfo, setOrderCheckoutInfo] = useState(null);
	const [salesPeople, setSalesPeople] = useState([]);
	const [countries, setCountries] = useState([]);
	const [provinces, setProvinces] = useState([]);
	const [userAddresses, setUserAddresses] = useState([]);
	const [collectionAddresses, setCollectionAddresses] = useState([]);
	const [createOrderErrorMessage, setCreateOrderErrorMessage] = useState("");

	const [checkoutDataCallStatus, setCheckoutDataCallStatus] = useState(ApiCallStatus.NotStarted);
	const [orderCheckoutInfoCallStatus, setOrderCheckoutInfoCallStatus] = useState(ApiCallStatus.NotStarted);
	const [addressCheckoutInfoCallStatus, setAddressCheckoutInfoCallStatus] = useState(ApiCallStatus.NotStarted);
	const [createOrderCallStatus, setCreateOrderCallStatus] = useState(ApiCallStatus.NotStarted);

	const gotoLoginPage = () => {
		return navigate("/login", {state: {from: location}});
	};
	const gotoCheckoutOrderInformation = () => navigate("/checkout/information");
	const gotoCheckoutBillingAddress = () => navigate("/checkout/billing");
	const gotoCheckoutCollectionAddress = () => navigate("/checkout/collection");
	const gotoCheckoutDeliveryMethod = () => navigate("/checkout/delivery-method");
	const gotoCheckoutShippingAddress = () => navigate("/checkout/shipping");
	const gotoPaymentPage = () => navigate(`/payment`);


	const fetchCheckoutDataAsync = async () => {
		await fetchCheckoutData({
			setCheckoutData,
			setStatusInProgress: () => setCheckoutDataCallStatus(ApiCallStatus.InProgress),
			setStatusSuccess: () => setCheckoutDataCallStatus(ApiCallStatus.Succeeded),
			setStatusFailed: () => setCheckoutDataCallStatus(ApiCallStatus.Failed),
			setStatusError: () => setCheckoutDataCallStatus(ApiCallStatus.Error)
		});
	};

	const fetchCheckoutAddressDataAsync = async () => {
		await fetchAddressCheckoutData({
			setAddressCheckoutInfo,
			setStatusInProgress: () => setAddressCheckoutInfoCallStatus(ApiCallStatus.InProgress),
			setStatusSuccess: () => setAddressCheckoutInfoCallStatus(ApiCallStatus.Succeeded),
			setStatusFailed: () => setAddressCheckoutInfoCallStatus(ApiCallStatus.Failed),
			setStatusError: () => setAddressCheckoutInfoCallStatus(ApiCallStatus.Error)
		});
	};

	const fetchCheckoutOrderDataAsync = async () => {
		await fetchOrderCheckoutData({
			setOrderCheckoutInfo,
			setStatusInProgress: () => setOrderCheckoutInfoCallStatus(ApiCallStatus.InProgress),
			setStatusSuccess: () => setOrderCheckoutInfoCallStatus(ApiCallStatus.Succeeded),
			setStatusFailed: () => setOrderCheckoutInfoCallStatus(ApiCallStatus.Failed),
			setStatusError: () => setOrderCheckoutInfoCallStatus(ApiCallStatus.Error)
		});
	};

	const createOrderDataAsync = async (checkoutData) => {
		setCreateOrderErrorMessage("");
		if (!!items) {
			await createOrderData({
				salesPersonCode: checkoutData.salesPersonCode,
				logisticsMethod: checkoutData.logisticsMethod,
				billingAddressId: checkoutData.billingAddressId,
				shippingAddressId: checkoutData.shippingAddressId,
				collectionAddressId: checkoutData.collectionAddressId,
				items,
				setStatusInProgress: () => setCreateOrderCallStatus(ApiCallStatus.InProgress),
				setStatusSuccess: () => setCreateOrderCallStatus(ApiCallStatus.Succeeded),
				setStatusFailed: () => setCreateOrderCallStatus(ApiCallStatus.Failed),
				setStatusError: () => setCreateOrderCallStatus(ApiCallStatus.Error),
				setErrorMessage: setCreateOrderErrorMessage
			});
		}
	};

	const authorizedPageLoad = async () => {
		await Promise.all([
			fetchCheckoutAddressDataAsync()
		]);
	};

	const unAuthorizedPageLoad = async () => {
		await Promise.all([
			fetchCheckoutOrderDataAsync(),
			fetchCheckoutDataAsync()
		]);
	};

	useEffect(() => {
		if (genericDataLoaded === true && isLoading === false) {
			if (isAuthenticated === false) {
				gotoLoginPage();
			} else {
				authorizedPageLoad();
				unAuthorizedPageLoad();
			}
		}

	}, [genericDataLoaded, isAuthenticated, isLoading]);

	useEffect(() => {
		if (addressCheckoutInfoCallStatus === ApiCallStatus.Succeeded && !!addressCheckoutInfo) {
			let userAddressesTemp = [];
			let collectionAddressesTemp = [];
			let countries = [];
			let provinces = [];

			if (addressCheckoutInfo?.countries) {
				const countriesInAlphabeticalOrder = _.sortBy(
					addressCheckoutInfo.countries,
					({name}) => name
				);
				countries = _.sortBy(countriesInAlphabeticalOrder, ({name}) =>
					name === "South Africa" ? 0 : 1
				);
			}

			if (addressCheckoutInfo?.provinces) {
				provinces = addressCheckoutInfo.provinces;
			}

			if (addressCheckoutInfo?.userAddresses) {
				userAddressesTemp = addressCheckoutInfo.userAddresses;
			}

			if (addressCheckoutInfo?.pickupLocations) {
				collectionAddressesTemp = addressCheckoutInfo.pickupLocations;
			}

			setProvinces(provinces);
			setCountries(countries);
			setUserAddresses(userAddressesTemp);
			setCollectionAddresses(collectionAddressesTemp);
		}
	}, [addressCheckoutInfo, addressCheckoutInfoCallStatus]);

	useEffect(() => {
		if (orderCheckoutInfoCallStatus === ApiCallStatus.Succeeded && !!orderCheckoutInfo?.salesPeople) {
			let salesPeopleTemp;
			salesPeopleTemp = orderCheckoutInfo.salesPeople;
			setSalesPeople(salesPeopleTemp);
		}
	}, [orderCheckoutInfo?.salesPeople, orderCheckoutInfoCallStatus]);

	useEffect(() => {
		if (createOrderCallStatus === ApiCallStatus.Succeeded) {
			emptyCart();
			gotoPaymentPage();
		}
	}, [createOrderCallStatus]);

	useEffect(() => {
		// if (!!createOrderErrorMessage) window.scrollTo(0, 0);
	}, [createOrderErrorMessage]);

	const allLoadingStates = [
		checkoutDataCallStatus,
		orderCheckoutInfoCallStatus,
		addressCheckoutInfoCallStatus,
		createOrderCallStatus
	];

	const pageIsLoading = allLoadingStates.includes(ApiCallStatus.InProgress) || genericDataLoaded === false || isLoading === true;

	return (
		<Container maxWidth="lg">
			{!!createOrderErrorMessage && (
				<Alert severity="warning" sx={{mt: 2, mb: 2}}>
					{createOrderErrorMessage}
				</Alert>
			)}
			<CheckoutSummary
				accountType={genericPageContent?.accountType}
				tecfinityAccountNumber={genericPageContent?.tecfinityAccountNumber}
				tecfinityAccountName={genericPageContent?.tecfinityAccountName}
				checkoutData={checkoutData}
				countries={countries}
				provinces={provinces}
				userAddresses={userAddresses}
				collectionAddresses={collectionAddresses}
				salesPeople={salesPeople}
				firstName={genericPageContent.firstName}
				lastName={genericPageContent.lastName}
				gotoCheckoutOrderInformation={gotoCheckoutOrderInformation}
				gotoCheckoutBillingAddress={gotoCheckoutBillingAddress}
				gotoCheckoutCollectionAddress={gotoCheckoutCollectionAddress}
				gotoCheckoutDeliveryMethod={gotoCheckoutDeliveryMethod}
				gotoCheckoutShippingAddress={gotoCheckoutShippingAddress}
				onSubmit={createOrderDataAsync}
			/>
			<Backdrop
				sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}}
				open={pageIsLoading}
			>
				<CircularProgress color="inherit"/>
			</Backdrop>
		</Container>
	);
};

export default CheckoutSummaryPage;
